import { requestFitplus } from '@/api';
import type {
    CurrentSubscriptionResDto,
    ReportCompletionGageResDto,
    ReportPublishingTrackingData,
    TimeSeriesResDto,
    TrendReportFeedbackQuestionDto,
    TrendReportResDto,
    UserFeedback,
} from '@common/openapi-client/openapi';
import type { RequestFeedbackData } from '../types/proReport';
import type { ApiResponse } from '../types/response';

const endPoint = {
    GET_IS_REPORT_VISIBLE: 'trend-report/has-trendreport-history',
    GET_TIMESERIES: 'trend-report/time-series',
    GET_MODULE_DATA: `trend-report/module-data`,
    GET_COMPLETION_GAGE: 'trend-report/report-completion-gage',
    GET_SUBSCRIPTION_STATUS: 'subscription/current-state',
    GET_REPORT_EXISTING_END_DATES: 'trend-report/report-existing-end-dates?now=#NOW#',
    GET_FEEDBACK_QUESTION: `trend-report/feedback/question`,
    POST_FEEDBACK_ANSWER: 'trend-report/feedback/answer',
    POST_SUBJECTIVE_FEEDBACK: 'trend-report/feedback/subjective',
    GET_ELIGIBILITY_TRACKING_CARD_DATA: 'trend-report/new-report-publish-tracking?now=#NOW#',
    POST_CLICK_REPORT_PUBLISH_BUTTON:
        'trend-report/click-report-publish-button?GenerationDate=#GENERATION_DATE#&ReportType=#REPORT_TYPE#',
    GET_TREND_REPORT_SHARE_TOKEN: `trend-report/share-token`,
    GET_TREND_REPORT_TIMESERIES_FOR_SHARE: `trend-report/shared/time-series`,
    GET_TREND_REPORT_MODULEDATA_FOR_SHARE: `trend-report/shared/module-data`,
};

export const getIsReportVisibleApi = () => {
    return requestFitplus<boolean>(endPoint.GET_IS_REPORT_VISIBLE, 'get');
};

export const getTimeseriesDataApi = (
    endDate: string,
    now: string | undefined,
    reportType = 'Month',
): Promise<ApiResponse<TimeSeriesResDto>> => {
    let url = endPoint.GET_TIMESERIES;

    return requestFitplus<TimeSeriesResDto>(url, 'get', {
        EndDate: endDate,
        ReportType: reportType,
        now: now,
    });
};

export const getModuleDataApi = (
    date: string,
    reportType = 'Month',
    now: string | undefined,
): Promise<ApiResponse<TrendReportResDto>> => {
    let url = endPoint.GET_MODULE_DATA;

    return requestFitplus<TrendReportResDto>(url, 'get', {
        EndDate: date,
        ReportType: reportType,
        now: now,
    });
};

export const getReportCompletionGageApi = () => {
    return requestFitplus<ReportCompletionGageResDto>(endPoint.GET_COMPLETION_GAGE, 'get');
};

export const getSubscriptionStatusApi = () => {
    return requestFitplus<CurrentSubscriptionResDto>(endPoint.GET_SUBSCRIPTION_STATUS, 'get');
};

export const getFeedbackQuestionApi = (generationDate: string, isPositive: boolean, reportType: string) => {
    return requestFitplus<TrendReportFeedbackQuestionDto>(endPoint.GET_FEEDBACK_QUESTION, 'get', {
        GenerationDate: generationDate,
        IsPositive: isPositive,
        ReportType: reportType,
    });
};

export const postFeedbackAnswerApi = (GenerationDate: string, answer: RequestFeedbackData, ReportType: string) => {
    return requestFitplus<UserFeedback>(endPoint.POST_FEEDBACK_ANSWER, 'post', {
        GenerationDate,
        ReportType,
        ...answer,
    });
};

export const postSubjectiveFeedbackApi = (
    GenerationDate: string,
    ReportType: string,
    SubjectiveAnswerValue: string,
) => {
    return requestFitplus<boolean>(endPoint.POST_SUBJECTIVE_FEEDBACK, 'post', {
        GenerationDate,
        ReportType,
        SubjectiveAnswerValue,
    });
};

export const getReportExistingEndDatesApi = (now: string | undefined): Promise<ApiResponse<string[]>> => {
    let url = endPoint.GET_REPORT_EXISTING_END_DATES;

    if (now) {
        url = url.replace('#NOW#', now);
    } else {
        url = url.replace('?now=#NOW#', '');
    }

    return requestFitplus<string[]>(url, 'get');
};

export const getEligibilityTrackingCardDataApi = (
    now: string | undefined,
): Promise<ApiResponse<ReportPublishingTrackingData>> => {
    let url = endPoint.GET_ELIGIBILITY_TRACKING_CARD_DATA;

    if (now) {
        url = url.replace('#NOW#', now);
    } else {
        url = url.replace('?now=#NOW#', '');
    }

    return requestFitplus<ReportPublishingTrackingData>(url, 'get');
};

export const postClickReportPublishButtonApi = (
    generationDateTime: string,
    reportType: string,
): Promise<ApiResponse<boolean>> => {
    const url = endPoint.POST_CLICK_REPORT_PUBLISH_BUTTON.replace('#GENERATION_DATE#', generationDateTime).replace(
        '#REPORT_TYPE#',
        reportType,
    );
    return requestFitplus<boolean>(url, 'post');
};

export const getTrendReportShareTokenApi = (endDate: string, ReportType: 'Month' | 'Week') => {
    const url = endPoint.GET_TREND_REPORT_SHARE_TOKEN;
    return requestFitplus<string>(url, 'get', {
        EndDate: endDate,
        ReportType: ReportType,
    });
};

export const getTrendReportTimeseriesForShareApi = (shareToken: string) => {
    // requestFitplus 호출 시 data를 넘기면, GET일 때 params로 들어가도록 이미 구현되어 있음
    return requestFitplus<TimeSeriesResDto>(endPoint.GET_TREND_REPORT_TIMESERIES_FOR_SHARE, 'get', {
        ShareToken: shareToken,
    });
};

export const getTrendReportModuleDataForShareApi = (shareToken: string) => {
    return requestFitplus<TrendReportResDto>(endPoint.GET_TREND_REPORT_MODULEDATA_FOR_SHARE, 'get', {
        ShareToken: shareToken,
    });
};
